
/**
 * The instant estimate panel. AC-2.3.
 *
 * The local engine renders immediately so the number never lags a keystroke,
 * and the server estimate is reconciled behind it. Constitution §1: the client
 * number is a preview, the server's is the price, so if they disagree the
 * server value is what gets shown and the difference is called out rather than
 * hidden.
 */

import { ArrowsClockwise, WarningCircle } from '@phosphor-icons/react';

import { Badge, Callout } from '@/components/ui';
import { formatDollars } from '@/lib/money';
import type { Estimate } from '@/lib/pricing/engine';

export function EstimatePanel({
  local,
  server,
  reconciling,
}: {
  local: Estimate;
  server: Estimate | null;
  reconciling: boolean;
}) {
  // The server wins when it has spoken.
  const shown = server ?? local;
  const drifted = server != null && server.total !== local.total;

  return (
    <div className="rounded-[10px] border border-brand/30 bg-brand-wash-2 p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-brand-ink">
          Instant estimate
        </p>
        <div className="flex items-center gap-2">
          {reconciling && (
            <ArrowsClockwise
              size={15}
              weight="bold"
              className="animate-spin text-muted"
              aria-label="Checking the price with the server"
            />
          )}
          {shown.tier_discount > 0 && (
            <Badge tone="brand">&minus;{shown.tier_discount}% volume</Badge>
          )}
        </div>
      </div>

      <p
        className="mt-2 text-[34px] font-extrabold tracking-[-0.03em]"
        aria-live="polite"
        aria-atomic="true"
      >
        {formatDollars(shown.total)}
      </p>
      <p className="text-sm text-muted">
        {formatDollars(shown.unit_price)} / unit &times; {shown.qty}
        {shown.setup_total > 0 && ` + ${formatDollars(shown.setup_total)} setup`}
      </p>

      <dl className="mt-4 space-y-1.5 border-t border-dashed border-brand/30 pt-4 text-[15px]">
        <div className="flex justify-between gap-4">
          <dt className="text-muted">Garment</dt>
          <dd className="font-semibold">{formatDollars(shown.garment_price)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted">
            Print ({shown.locations.length}{' '}
            {shown.locations.length === 1 ? 'location' : 'locations'}
            {shown.colours != null &&
              `, ${shown.colours} ${shown.colours === 1 ? 'colour' : 'colours'}`}
            )
          </dt>
          <dd className="font-semibold">{formatDollars(shown.print_per_unit)}/unit</dd>
        </div>
        {shown.setup_total > 0 && (
          <div className="flex justify-between gap-4">
            <dt className="text-muted">Setup</dt>
            <dd className="font-semibold">{formatDollars(shown.setup_total)}</dd>
          </div>
        )}
        <div className="flex justify-between gap-4">
          <dt className="text-muted">Turnaround</dt>
          <dd className="font-semibold">{shown.turnaround}</dd>
        </div>
      </dl>

      <p className="mt-4 text-[13px] text-muted">
        GST inclusive. This is a ballpark, confirmed when we price the quote.
      </p>

      {shown.warnings.length > 0 && (
        <div className="mt-4 space-y-2">
          {shown.warnings.map((w) => (
            <Callout key={w} tone="warn" title={w}>
              {w.includes('minimum order')
                ? 'You can still send the quote through. We will work it out when we price it.'
                : undefined}
            </Callout>
          ))}
        </div>
      )}

      {drifted && (
        <div className="mt-4">
          <Callout tone="warn" title="Price updated">
            <span className="inline-flex items-center gap-1.5">
              <WarningCircle size={15} weight="fill" aria-hidden />
              Our rates changed while you were designing. The figure above is the current
              one.
            </span>
          </Callout>
        </div>
      )}
    </div>
  );
}
